import * as React from 'react';
import { Effect } from 'effect';
import { attemptStudioPromise, runStudioPromise } from '../effect-runtime';
import { trackPdfjsDestroy, type PdfjsDocument } from './pdfjs';
import {
  openPdfjsDocumentEffect,
  renderPageCanvasEffect,
  renderTextLayerHandleEffect,
  getPageSizeEffect,
  type RenderPageOptions,
  type RenderTextLayerHandle,
} from './pdfjs-operations';
export type { RenderPageOptions, RenderTextLayerHandle } from './pdfjs-operations';
import { downloadBlob } from './download';
import { formatBytes } from './utils';
import { mergeExtraTools, toolsByCategory } from './registry';
import type { StudioTool, StudioToolCategory } from './tool';
import type { StudioComputeDocument, StudioGraph } from './api';
import { studioErrorMetadata, type StudioEvent, type StudioEventSink } from './analytics';
import {
  STUDIO_STAGED_UNDO_REASON,
  nextStudioUndoHistory,
  planStudioUndo,
  studioUndoLabel,
  studioUndoneNotice,
  type StudioUndoChange,
  type StudioUndoStep,
} from './undo-history';

/** Minimal identity of the active tool, tracked for analytics attribution. */
export interface ActiveToolRef {
  id: string;
  category: StudioToolCategory;
}

/**
 * The active tool's interaction layer on the MAIN viewport canvas (W.4.41).
 * Interactive tools register this instead of embedding a second PDF preview
 * in the side panel: pointer events on each rendered page are converted to
 * normalized page coordinates (0..1, top-left origin) and routed to the
 * handlers, and `render` draws the tool's annotations/drafts on that page.
 * Coordinates are resolution-independent — the same rect works at any zoom.
 */
export interface StudioCanvasLayer {
  /** CSS cursor shown over page surfaces while the layer is active. */
  cursor?: string;
  /** Per-page annotation/draft content, absolutely positioned over the page. */
  render?: (pageNumber: number) => React.ReactNode;
  onPointerDown?: (pageNumber: number, x: number, y: number) => void;
  onPointerMove?: (pageNumber: number, x: number, y: number) => void;
  onPointerUp?: (pageNumber: number, x: number, y: number) => void;
  /** The browser took the pointer (a touch became a scroll): drop the gesture. */
  onPointerCancel?: (pageNumber: number) => void;
  /**
   * Draw mode, for a layer that draws by dragging (W.4.132). Left out, a layer
   * with pointer handlers takes every touch on the page, as before. Given, a
   * touch draws only while `active` and scrolls the page at other times (a
   * mouse or pen draws either way), and below `sm` the tool sheet folds to a
   * bar that shows `label`, so the page is in view. `end` turns it off: the
   * bar's Cancel, and Escape.
   */
  drawMode?: { active: boolean; label: string; end: () => void };
  /**
   * Receives a browser text selection from the PDF.js text layer. Layers that
   * only implement this callback remain pointer-transparent so native text
   * selection/copy continues to work on the page.
   */
  onTextSelection?: (pageNumber: number, selection: StudioTextSelection) => void;
}

export interface StudioTextSelectionRect {
  x: number;
  y: number;
  width: number;
  height: number;
}

export interface StudioTextSelection {
  text: string;
  rects: StudioTextSelectionRect[];
}

export interface WorkspaceDoc {
  name: string;
  bytes: Uint8Array;
  pageCount: number;
  sizeLabel: string;
}

export type StudioRewriteInstruction = 'fix' | 'shorten' | 'clarify';

/**
 * How the open document changed, for the host (W.4.132). `edit` is true when
 * the new bytes are a tool's change to the document that was open (Apply, Undo,
 * Apply and download), and false when another document was opened (a file, a
 * conversion, a merge). A host that keys state by document keeps the key
 * across edits.
 */
export interface StudioDocumentChange {
  edit: boolean;
}

/**
 * Host AI seam for Edit text: rewrite one text block. The package never calls
 * a model itself — a host that wants the rewrite row passes this to StudioApp.
 */
export type StudioRewriteText = (input: {
  text: string;
  instruction: StudioRewriteInstruction;
  /** Upper bound for the reply, derived from the block (the page does not reflow). */
  maxChars: number;
}) => Promise<string>;

export interface StudioContextValue {
  doc: WorkspaceDoc | null;
  /** Tool categories hidden from the UI (labelling-level exclusions). */
  excludeCategories: StudioToolCategory[];
  /** Hide every `requiresCompute` tool — hosts without the `/_emdash/api/studio/*` backend. */
  hideComputeTools: boolean;
  /**
   * Host-injected tools merged over the built-in registry (rail, `?tool=`
   * deep links, host selection seam). Lets a host offer tools the shared
   * registry doesn't own — e.g. okrapdf.com's cloud-backed runners — without
   * the package importing host code.
   */
  extraTools: StudioTool[];
  /** The currently selected tool id, or null on the empty state. */
  activeToolId: string | null;
  /** Select (or clear) the active tool. Emits `tool_viewed` to the host sink. */
  setActiveTool: (tool: ActiveToolRef | null) => void;
  /**
   * The active tool's main-canvas interaction layer (W.4.41), or null when no
   * tool is editing on the viewport. Tool panels set it on mount and clear it
   * on unmount.
   */
  canvasLayer: StudioCanvasLayer | null;
  setCanvasLayer: (layer: StudioCanvasLayer | null) => void;
  /** True when the open document has been modified by a tool and not yet downloaded. */
  dirty: boolean;
  /**
   * True while the active tool holds edits that Apply has not written: boxes
   * drawn, text typed, fields filled. They are as unsaved as `dirty`, but they
   * live in the panel, so only the tool can say. Panels report them through
   * `useStudioStagedWork`, with the short reason Undo shows while they wait.
   */
  stagedWork: boolean;
  setStagedWork: (staged: boolean, reason?: string) => void;
  /**
   * One undo history for the open document (W.4.132). Every open with `edit`
   * keeps the document it replaced, with the active tool's title; another
   * document, or closing this one, empties it. True while there is a step
   * and nothing blocks it.
   */
  canUndo: boolean;
  /** The Undo control's name for the step on top ("Undo Watermark"), or null with none. */
  undoLabel: string | null;
  /**
   * Why the step on top cannot be undone now, or null: the active tool holds
   * staged work, which belongs to the pages Undo would replace.
   */
  undoBlocked: string | null;
  /**
   * Restore the document from before the last edit, through `openBytes` with
   * `edit`, with its own unsaved flag, and say so with `notify`. Staged work
   * stops it and the reason is the notice. Resolves true when a step was undone.
   */
  undo: () => Promise<boolean>;
  /**
   * Open bytes as the document. `dirty` marks them unsaved; `edit` says they
   * change the document that was open (see `StudioDocumentChange`), and is
   * `dirty` when left out.
   */
  openBytes: (
    bytes: Uint8Array,
    name: string,
    options?: { dirty?: boolean; edit?: boolean },
  ) => Promise<void>;
  openFile: (file: File) => Promise<void>;
  closeDocument: () => void;
  download: (bytes: Uint8Array | Blob, name: string, type?: string) => void;
  downloadCurrent: () => void;
  /** Render a 1-based page to a fresh canvas at the given scale (1 = 72dpi). */
  renderPage: (
    pageNumber: number,
    scale: number,
    options?: RenderPageOptions,
  ) => Promise<HTMLCanvasElement>;
  /** Render the matching selectable PDF.js text layer into an existing container. */
  renderTextLayer: (
    pageNumber: number,
    scale: number,
    container: HTMLDivElement,
    options?: RenderPageOptions,
  ) => Promise<RenderTextLayerHandle>;
  /** Page size in CSS pixels at scale 1 (PDF points). */
  getPageSize: (pageNumber: number) => Promise<{ width: number; height: number }>;
  /** AI graph loaded by the AI tools; rendered as bbox overlays on the viewport. */
  graph: StudioGraph | null;
  setGraph: (graph: StudioGraph | null) => void;
  /** Currently parsed compute-side document (set by AI Parse, consumed by other AI tools). */
  computeDoc: StudioComputeDocument | null;
  setComputeDoc: (doc: StudioComputeDocument | null) => void;
  /** Transient status toast shown in the status bar. */
  notice: string | null;
  notify: (message: string) => void;
  /**
   * Report a user-visible tool failure to the host analytics sink (`tool_error`),
   * attributed to the active tool. Message text only — never file data.
   */
  reportError: (error: { code?: string; message?: string }) => void;
  /** Host text-rewrite seam, or null when the host offers none (the row is not rendered). */
  rewriteText: StudioRewriteText | null;
}

const StudioContext = React.createContext<StudioContextValue | null>(null);

/** For `StudioPreview` only: a host mounts `WorkspaceProvider` or `StudioPreview`, never this. */
export const StudioContextProvider = StudioContext.Provider;

const NO_EXCLUSIONS: StudioToolCategory[] = [];
const NO_EXTRA_TOOLS: StudioTool[] = [];

export function WorkspaceProvider({
  children,
  excludeCategories = NO_EXCLUSIONS,
  hideComputeTools = false,
  extraTools = NO_EXTRA_TOOLS,
  onEvent,
  onDocumentChange,
  rewriteText,
}: {
  children: React.ReactNode;
  excludeCategories?: StudioToolCategory[];
  /** Hide compute-worker-backed tools (hosts that don't serve `/_emdash/api/studio/*`). */
  hideComputeTools?: boolean;
  /** Host-injected tools merged over the built-in registry (never mutates it). */
  extraTools?: StudioTool[];
  /** Host analytics sink. Receives tool-metadata-only events; never file data. */
  onEvent?: StudioEventSink;
  /** Host state bridge notified whenever the active Studio PDF changes or closes. */
  onDocumentChange?: (doc: WorkspaceDoc | null, change?: StudioDocumentChange) => void;
  /** Host AI seam for Edit text. Absent → `rewriteText` is null in the context. */
  rewriteText?: StudioRewriteText;
}) {
  const [doc, setDoc] = React.useState<WorkspaceDoc | null>(null);
  const [dirty, setDirty] = React.useState(false);
  // The staged-work reason, or null while no tool holds staged edits.
  const [staged, setStaged] = React.useState<string | null>(null);
  const stagedRef = React.useRef<string | null>(null);
  stagedRef.current = staged;
  const setStagedWork = React.useCallback((on: boolean, reason?: string) => {
    setStaged(on ? (reason ?? STUDIO_STAGED_UNDO_REASON) : null);
  }, []);
  // The document and its unsaved flag as the last open left them, read when
  // the next edit keeps them as an undo step.
  const docRef = React.useRef<WorkspaceDoc | null>(null);
  const dirtyRef = React.useRef(false);
  const historyRef = React.useRef<StudioUndoStep[]>([]);
  const [undoTop, setUndoTop] = React.useState<StudioUndoStep | null>(null);
  const undoingRef = React.useRef(false);
  const extraToolsRef = React.useRef(extraTools);
  extraToolsRef.current = extraTools;
  const [graph, setGraph] = React.useState<StudioGraph | null>(null);
  const [computeDoc, setComputeDoc] = React.useState<StudioComputeDocument | null>(null);
  const [notice, setNotice] = React.useState<string | null>(null);
  const [activeToolId, setActiveToolId] = React.useState<string | null>(null);
  const [canvasLayer, setCanvasLayer] = React.useState<StudioCanvasLayer | null>(null);
  const pdfjsDocRef = React.useRef<PdfjsDocument | null>(null);
  const mountedRef = React.useRef(true);
  const epochRef = React.useRef(0);
  const documentLifetimeRef = React.useRef(new AbortController());
  const textHandlesRef = React.useRef(new Set<RenderTextLayerHandle>());
  const noticeTimerRef = React.useRef<number | null>(null);
  // Latest active tool + sink kept in refs so download()/openFile() can attribute
  // events without those callbacks re-identifying on every selection.
  const activeToolRef = React.useRef<ActiveToolRef | null>(null);
  const onEventRef = React.useRef<StudioEventSink | undefined>(onEvent);
  onEventRef.current = onEvent;
  const onDocumentChangeRef = React.useRef(onDocumentChange);
  onDocumentChangeRef.current = onDocumentChange;
  // Hosts tend to pass a fresh closure each render. The context hands out one
  // stable caller that reads the latest, so only present ↔ absent re-renders
  // the consumers.
  const rewriteTextRef = React.useRef(rewriteText);
  rewriteTextRef.current = rewriteText;
  const hasRewriteText = Boolean(rewriteText);
  const stableRewriteText = React.useMemo<StudioRewriteText | null>(
    () =>
      hasRewriteText
        ? (input) =>
            rewriteTextRef.current?.(input) ??
            Promise.reject(new Error('Text rewrite is not available'))
        : null,
    [hasRewriteText],
  );

  const emit = React.useCallback((event: StudioEvent) => {
    onEventRef.current?.(event);
  }, []);

  const setActiveTool = React.useCallback(
    (tool: ActiveToolRef | null) => {
      activeToolRef.current = tool;
      setActiveToolId(tool?.id ?? null);
      if (tool) emit({ name: 'tool_viewed', tool: tool.id, category: tool.category });
    },
    [emit],
  );

  const destroyPdfjsDoc = React.useCallback(() => {
    epochRef.current += 1;
    documentLifetimeRef.current.abort();
    documentLifetimeRef.current = new AbortController();
    for (const handle of textHandlesRef.current) handle.cancel();
    textHandlesRef.current.clear();
    const previous = pdfjsDocRef.current;
    pdfjsDocRef.current = null;
    // Fire-and-forget the destroy itself, but register it so the next
    // getDocument waits for it instead of racing the shared worker's
    // _pendingDestroy window (issue #979).
    if (previous) trackPdfjsDestroy(previous.destroy());
  }, []);

  const changeHistory = React.useCallback((change: StudioUndoChange) => {
    historyRef.current = nextStudioUndoHistory(historyRef.current, change);
    setUndoTop(historyRef.current.at(-1) ?? null);
  }, []);

  // The title of the tool an edit comes from, for the step's "Undo <title>".
  const activeToolTitle = React.useCallback((): string | null => {
    const id = activeToolRef.current?.id;
    if (!id) return null;
    return mergeExtraTools(extraToolsRef.current).find((tool) => tool.id === id)?.title ?? null;
  }, []);

  const openSource = React.useCallback(
    (
      source: Effect.Effect<Uint8Array, import('../effect-runtime').StudioRuntimeError>,
      name: string,
      options?: { dirty?: boolean; edit?: boolean; undo?: StudioUndoStep },
    ) => {
      // A host action that resolves after the Studio is gone (a slow download,
      // a route change) must not open a document nobody will ever close.
      if (!mountedRef.current) return Promise.reject(new Error('studio unmounted'));
      destroyPdfjsDoc();
      const lifetime = documentLifetimeRef.current;
      return runStudioPromise(
        source.pipe(
          Effect.flatMap((bytes) =>
            openPdfjsDocumentEffect(bytes, (pdfDoc) => {
              if (lifetime.signal.aborted) throw new Error('document open cancelled');
              pdfjsDocRef.current = pdfDoc;
              const nextDoc = {
                name,
                bytes,
                pageCount: pdfDoc.numPages,
                sizeLabel: formatBytes(bytes.byteLength),
              };
              const edit = options?.edit ?? options?.dirty ?? false;
              const previous = docRef.current;
              changeHistory(
                options?.undo
                  ? { kind: 'undo', step: options.undo }
                  : edit && previous
                    ? {
                        kind: 'edit',
                        previous: {
                          bytes: previous.bytes,
                          name: previous.name,
                          dirty: dirtyRef.current,
                          tool: activeToolTitle(),
                        },
                      }
                    : { kind: 'open' },
              );
              docRef.current = nextDoc;
              dirtyRef.current = options?.dirty ?? false;
              setDoc(nextDoc);
              setDirty(dirtyRef.current);
              onDocumentChangeRef.current?.(nextDoc, { edit });
            }),
          ),
        ),
        { signal: lifetime.signal },
      );
    },
    [destroyPdfjsDoc, changeHistory, activeToolTitle],
  );

  const openBytes = React.useCallback(
    (bytes: Uint8Array, name: string, options?: { dirty?: boolean; edit?: boolean }) =>
      openSource(Effect.succeed(bytes), name, options),
    [openSource],
  );

  const openFile = React.useCallback(
    (file: File) =>
      openSource(
        attemptStudioPromise('pdfjs', 'file-read', () => file.arrayBuffer()).pipe(
          Effect.map((bytes) => new Uint8Array(bytes)),
        ),
        file.name,
      ).then(() => {
        const active = activeToolRef.current;
        emit({ name: 'tool_file_opened', tool: active?.id, category: active?.category });
      }),
    [openSource, emit],
  );

  const closeDocument = React.useCallback(() => {
    destroyPdfjsDoc();
    changeHistory({ kind: 'open' });
    docRef.current = null;
    dirtyRef.current = false;
    setDoc(null);
    setDirty(false);
    setGraph(null);
    setComputeDoc(null);
    onDocumentChangeRef.current?.(null);
  }, [destroyPdfjsDoc, changeHistory]);

  const reportError = React.useCallback(
    ({ code }: { code?: string; message?: string }) => {
      const active = activeToolRef.current;
      emit({
        name: 'tool_error',
        tool: active?.id,
        category: active?.category,
        ...studioErrorMetadata(code),
      });
    },
    [emit],
  );

  const emitToolRun = React.useCallback(() => {
    const active = activeToolRef.current;
    emit({ name: 'tool_run', tool: active?.id, category: active?.category });
  }, [emit]);

  const download = React.useCallback(
    (bytes: Uint8Array | Blob, name: string, type?: string) => {
      downloadBlob(bytes, name, type);
      emitToolRun();
    },
    [emitToolRun],
  );

  const downloadCurrent = React.useCallback(() => {
    setDoc((current) => {
      if (current) {
        downloadBlob(
          current.bytes,
          current.name.toLowerCase().endsWith('.pdf') ? current.name : `${current.name}.pdf`,
        );
        dirtyRef.current = false;
        setDirty(false);
        emitToolRun();
      }
      return current;
    });
  }, [emitToolRun]);

  const renderPage = React.useCallback(
    async (
      pageNumber: number,
      scale: number,
      options?: RenderPageOptions,
    ): Promise<HTMLCanvasElement> => {
      const pdfDoc = pdfjsDocRef.current;
      if (!pdfDoc) throw new Error('No document open');
      const epoch = epochRef.current;
      return runStudioPromise(
        renderPageCanvasEffect(
          pdfDoc,
          pageNumber,
          scale,
          () => Boolean(options?.cancelled?.()) || epoch !== epochRef.current,
        ),
        {
          signal: options?.signal
            ? AbortSignal.any([documentLifetimeRef.current.signal, options.signal])
            : documentLifetimeRef.current.signal,
        },
      );
    },
    [],
  );

  const renderTextLayer = React.useCallback(
    async (
      pageNumber: number,
      scale: number,
      container: HTMLDivElement,
      options?: RenderPageOptions,
    ): Promise<RenderTextLayerHandle> => {
      const pdfDoc = pdfjsDocRef.current;
      if (!pdfDoc) throw new Error('No document open');
      const epoch = epochRef.current;
      const handle = await runStudioPromise(
        renderTextLayerHandleEffect(
          pdfDoc,
          pageNumber,
          scale,
          container,
          () => Boolean(options?.cancelled?.()) || epoch !== epochRef.current,
        ),
        {
          signal: options?.signal
            ? AbortSignal.any([documentLifetimeRef.current.signal, options.signal])
            : documentLifetimeRef.current.signal,
        },
      );
      if (epoch !== epochRef.current || options?.signal?.aborted || options?.cancelled?.()) {
        handle.cancel();
        throw new Error('render cancelled');
      }
      const owned = {
        cancel: () => {
          textHandlesRef.current.delete(owned);
          handle.cancel();
        },
      };
      textHandlesRef.current.add(owned);
      return owned;
    },
    [],
  );

  const getPageSize = React.useCallback(async (pageNumber: number) => {
    const pdfDoc = pdfjsDocRef.current;
    if (!pdfDoc) throw new Error('No document open');
    return runStudioPromise(getPageSizeEffect(pdfDoc, pageNumber), {
      signal: documentLifetimeRef.current.signal,
    });
  }, []);

  const notify = React.useCallback((message: string) => {
    if (noticeTimerRef.current !== null) window.clearTimeout(noticeTimerRef.current);
    setNotice(message);
    noticeTimerRef.current = window.setTimeout(() => {
      setNotice(null);
      noticeTimerRef.current = null;
    }, 4000);
  }, []);

  const undo = React.useCallback(async (): Promise<boolean> => {
    if (undoingRef.current) return false;
    const plan = planStudioUndo(historyRef.current, stagedRef.current);
    if (plan.kind === 'nothing') return false;
    // Staged edits were made on the pages the step would replace.
    if (plan.kind === 'blocked') {
      notify(plan.reason);
      return false;
    }
    const { step } = plan;
    undoingRef.current = true;
    try {
      await openSource(Effect.succeed(step.bytes), step.name, { ...plan.open, undo: step });
      notify(studioUndoneNotice(step));
      return true;
    } catch {
      // Another open took over, or the Studio went away: the step stays for the next Undo.
      return false;
    } finally {
      undoingRef.current = false;
    }
  }, [openSource, notify]);

  React.useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
      if (noticeTimerRef.current !== null) window.clearTimeout(noticeTimerRef.current);
      destroyPdfjsDoc();
      historyRef.current = [];
      docRef.current = null;
      onDocumentChangeRef.current?.(null);
    };
  }, [destroyPdfjsDoc]);

  const stagedWork = staged !== null;
  const undoLabel = studioUndoLabel(undoTop ?? undefined);
  const undoBlocked = undoTop && staged !== null ? staged : null;
  const canUndo = undoTop !== null && !stagedWork;

  const value = React.useMemo<StudioContextValue>(
    () => ({
      doc,
      excludeCategories,
      hideComputeTools,
      extraTools,
      activeToolId,
      setActiveTool,
      canvasLayer,
      setCanvasLayer,
      dirty,
      stagedWork,
      setStagedWork,
      canUndo,
      undoLabel,
      undoBlocked,
      undo,
      openBytes,
      openFile,
      closeDocument,
      download,
      downloadCurrent,
      renderPage,
      renderTextLayer,
      getPageSize,
      graph,
      setGraph,
      computeDoc,
      setComputeDoc,
      notice,
      notify,
      reportError,
      rewriteText: stableRewriteText,
    }),
    [
      doc,
      excludeCategories,
      hideComputeTools,
      extraTools,
      activeToolId,
      setActiveTool,
      canvasLayer,
      setCanvasLayer,
      dirty,
      stagedWork,
      setStagedWork,
      canUndo,
      undoLabel,
      undoBlocked,
      undo,
      openBytes,
      openFile,
      closeDocument,
      download,
      downloadCurrent,
      renderPage,
      renderTextLayer,
      getPageSize,
      graph,
      computeDoc,
      notice,
      notify,
      reportError,
      stableRewriteText,
    ],
  );

  return <StudioContext.Provider value={value}>{children}</StudioContext.Provider>;
}

export function useStudio(): StudioContextValue {
  const context = React.useContext(StudioContext);
  if (!context) throw new Error('useStudio must be used inside <WorkspaceProvider>');
  return context;
}

export interface StudioTools {
  /** Registry tools minus the provider's `excludeCategories`. */
  tools: StudioTool[];
  getTool: (id: string | null | undefined) => StudioTool | null;
  toolsByCategory: () => Array<{ category: StudioToolCategory; tools: StudioTool[] }>;
}

/** Registry accessors merged with host-injected tools, filtered by host visibility. */
export function useStudioTools(): StudioTools {
  const { excludeCategories, hideComputeTools, extraTools } = useStudio();
  return React.useMemo(() => {
    const excluded = new Set<StudioToolCategory>(excludeCategories);
    const visible = (tool: StudioTool) =>
      !excluded.has(tool.category) && !(hideComputeTools && tool.requiresCompute);
    const all = mergeExtraTools(extraTools);
    return {
      tools: all.filter(visible),
      getTool: (id) => {
        const tool = all.find((candidate) => candidate.id === id) ?? null;
        return tool && visible(tool) ? tool : null;
      },
      toolsByCategory: () => {
        const merged = new Map<StudioToolCategory, StudioTool[]>();
        for (const group of toolsByCategory()) {
          merged.set(group.category, group.tools.filter(visible));
        }
        for (const tool of extraTools) {
          if (!visible(tool)) continue;
          merged.set(tool.category, [...(merged.get(tool.category) ?? []), tool]);
        }
        return [...merged.entries()]
          .map(([category, tools]) => ({ category, tools }))
          .filter((group) => group.tools.length > 0);
      },
    };
  }, [excludeCategories, hideComputeTools, extraTools]);
}
